import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { useState } from "react";

import { Card, Col, Container, Dropdown, DropdownButton, Row } from "react-bootstrap";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const getGenderDistribution = (users: any, filterZipCode = null) => {
  const filteredUsers = filterZipCode ? users.filter((user: any) => user.homeZipCode === filterZipCode) : users;
  const counts = filteredUsers.reduce((acc: any, user: any) => {
    acc[user.gender] = (acc[user.gender] || 0) + 1;
    return acc;
  }, {});
  return Object.keys(counts).map((key) => ({ label: key, count: counts[key] }));
};

const getExperienceLevelDistribution = (users: any, filterZipCode = null) => {
  const filteredUsers = filterZipCode ? users.filter((user: any) => user.homeZipCode === filterZipCode) : users;
  const counts = filteredUsers.reduce((acc: any, user: any) => {
    acc[user.experienceLevel] = (acc[user.experienceLevel] || 0) + 1;
    return acc;
  }, {});
  return Object.keys(counts).map((key) => ({ label: key, count: counts[key] }));
};

// get age distribution by using their dateOfBirth timestamp field with filterZipCode
const getAgeDistribution = (users: any, filterZipCode = null) => {
  const filteredUsers = filterZipCode ? users.filter((user: any) => user.homeZipCode === filterZipCode) : users;
  const counts = filteredUsers.reduce((acc: any, user: any) => {
    if (user.dateOfBirth) {
      const age = new Date().getFullYear() - new Date(user.dateOfBirth).getFullYear();
      const ageGroup = `${Math.floor(age / 10) * 10}-${Math.floor(age / 10) * 10 + 9}`;
      acc[ageGroup] = (acc[ageGroup] || 0) + 1;
    }
    return acc;
  }, {});
  return Object.keys(counts).map((key) => ({ label: key, count: counts[key] }));
};

const getPreferredStrainDistribution = (users: any, filterZipCode = null) => {
  const filteredUsers = filterZipCode ? users.filter((user: any) => user.homeZipCode === filterZipCode) : users;
  const counts = filteredUsers.reduce((acc: any, user: any) => {
    acc[user.preferredStrain] = (acc[user.preferredStrain] || 0) + 1;
    return acc;
  }, {});
  return Object.keys(counts).map((key) => ({ label: key, count: counts[key] }));
};

export const GlobalUserAnalyticsDashboard = ({ users, zipCodes }: { users: any; zipCodes: any }) => {
  const [selectedZipCode, setSelectedZipCode] = useState<any>("all");
  const genderData = getGenderDistribution(users, selectedZipCode === "all" ? null : selectedZipCode);
  const experienceData = getExperienceLevelDistribution(users, selectedZipCode === "all" ? null : selectedZipCode);
  const strainData = getPreferredStrainDistribution(users, selectedZipCode === "all" ? null : selectedZipCode);
  const ageData = getAgeDistribution(users, selectedZipCode === "all" ? null : selectedZipCode);

  const handleSelectZipCode = (eventKey: any) => {
    setSelectedZipCode(eventKey);
  };

  return (
    <Container fluid>
      <Row></Row>
      <Row>
        <Col xs={12} className="mb-4">
          <DropdownButton
            title={`Filter by Zip Code: ${selectedZipCode}`}
            onSelect={handleSelectZipCode}
            id="dropdown-basic-button"
          >
            <Dropdown.Item eventKey="all">Show All</Dropdown.Item>
            {zipCodes.map((zip: any) => (
              <Dropdown.Item eventKey={zip} key={zip}>
                {zip}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </Col>
        {buildChart("Gender Distribution", genderData)}
        {buildChart("Experience Levels", experienceData)}
        {buildChart("Strain Preferences", strainData)}
        {buildChart("Age Distribution", ageData)}
      </Row>
    </Container>
  );
};

const setupChartData = (data: any) => {
  const labels = data.map((d: any) => d.label);
  const datasetData = data.map((d: any) => d.count);
  return {
    labels: labels,
    datasets: [
      {
        label: "Distribution",
        data: datasetData,
        backgroundColor: ["rgba(255, 99, 132, 0.5)", "rgba(54, 162, 235, 0.5)", "rgba(75, 192, 192, 0.5)"],
        borderColor: ["rgba(255, 99, 132, 1)", "rgba(54, 162, 235, 1)", "rgba(75, 192, 192, 1)"],
        borderWidth: 1,
      },
    ],
  };
};

const buildChart = (title: string, data: any) => (
  <Col md={4}>
    <Card>
      <Card.Body>
        <Card.Title>{title}</Card.Title>
        <Bar data={setupChartData(data)} />
      </Card.Body>
    </Card>
  </Col>
);
